import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Api} from '@shared/constants/common-field.constants';
import {PlatformCommand} from '@shared/commands/command-base';
import {CommandService} from '@core/services/command.service';
import {CdkDragDrop, copyArrayItem, moveItemInArray} from '@angular/cdk/drag-drop';
import {EliteFormFlowChartDataService} from '@features/elite-form/services/elite-form-flow-chart-data.service';
import {FlowChartDataService} from '@features/data-survey/services/flow-chart-data.service';
import {PlatformQuery} from "@shared/models/platform-query/platform-query";
import {PlatformQueryService} from "@core/services/platform-query.service";
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {EliteFormDesignService} from "@features/elite-form/services/elite-form-design.service";
import {getCurrentPlatformLink, ParticleState} from '@shared/store/reducers/particle.reduer';
import {Store} from '@ngrx/store';
import {MagicLinkModel} from '@shared/models/platform-tool-models';

@Component({
    standalone: false,
    selector: 'app-elite-form-canvas-design-container',
  templateUrl: './elite-form-canvas-design-container.component.html',
  styleUrls: ['./elite-form-canvas-design-container.component.scss']
})
export class EliteFormCanvasDesignContainerComponent implements OnInit, OnDestroy {
  @Input() eliteformId: string;
  componentId: 'form-canvas';
  sections: any[] = [];
  ngUnsubscribe$ = new Subject<void>();

  pages = [];
  questions = [];
  flowchartSections = [];
  currentPageIndex = 0;
  showPageOptions = false;
  private currentPlatformLink: MagicLinkModel;
  constructor(private commandService: CommandService,
              private eliteFormFlowChartDataService: EliteFormFlowChartDataService,
              private eliteFormDesignService: EliteFormDesignService,
              private particleStore: Store<ParticleState>,
              public flowChartDataService: FlowChartDataService,
              private platformQueryService: PlatformQueryService) { }

  ngOnInit(): void {
    this.particleStore.select(getCurrentPlatformLink).pipe(takeUntil(this.ngUnsubscribe$)).subscribe((platformLink: any) => {
      this.currentPlatformLink = platformLink;
    });
    this.getEliteFormPages().pipe(takeUntil(this.ngUnsubscribe$)).subscribe(response => {
      this.pages = response.fieldValues['EliteFormPages'];
      this.questions = response.fieldValues['Questions'];
      this.flowchartSections = response.fieldValues['FlowchartSections'];
      this.currentPageIndex = Math.min(this.currentPageIndex, Math.max(0, this.pages.length - 1));
      this.selectCurrentPage();
    });
    this.eliteFormFlowChartDataService.invalidatePagesSubject.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(o => {
      this.getEliteFormPages().pipe(takeUntil(this.ngUnsubscribe$)).subscribe(response => {
        this.pages = response.fieldValues['EliteFormPages'];
        this.questions = response.fieldValues['Questions'];
        this.flowchartSections = response.fieldValues['FlowchartSections'];
        const newIndex = Math.min(this.currentPageIndex, Math.max(0, this.pages.length - 1));
        this.currentPageIndex = -1;
        setTimeout(() => {
          this.currentPageIndex = newIndex;
          this.selectCurrentPage();
        }, 0);
      });
    });
  }

  getFlowchartSection(flowchartId: string) {
    return this.flowchartSections.find(section => section.id === flowchartId);
  }

  openDialogDefinition() {
    const createFieldCommand = new PlatformCommand('AddTextFieldCommand', Api.FileStoreApi);
    this.commandService.executePlatformCommand(createFieldCommand).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(response => {
      //
    });
  }
  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      copyArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }
  addPage() {
    this.eliteFormFlowChartDataService.addPage(this.eliteformId);
  }
  getEliteFormPages() {
    const apiName = this.eliteFormFlowChartDataService.getApi();
    const query = new PlatformQuery();
    query.name ='EliteFormPageModelsQuery';
    query.api = apiName;
    query.fieldValues['EliteFormId'] = this.eliteformId;
    query.fieldValues['IsClientSchema'] = this.currentPlatformLink.fieldValues['IsClientSchema'];
    query.fieldValues['SchemaId'] = this.eliteFormFlowChartDataService.schemaId;
    query.fieldValues['IsClientSchema'] = this.eliteFormFlowChartDataService.isClientSchema;
    return this.platformQueryService.executeQuery(query);
  }

  renamePage() {
    //
  }

  goToNextPage() {
    const nextIndex = this.currentPageIndex + 1;
    if (nextIndex < this.pages.length) {
      this.currentPageIndex = -1;
      setTimeout(() => {
        this.currentPageIndex = nextIndex;
        this.showPageOptions = false;
        this.selectCurrentPage();
      }, 0);
    }
  }

  goToPreviousPage() {
    const prevIndex = this.currentPageIndex - 1;
    if (prevIndex >= 0) {
      this.currentPageIndex = -1;
      setTimeout(() => {
        this.currentPageIndex = prevIndex;
        this.showPageOptions = false;
        this.selectCurrentPage();
      }, 0);
    }
  }

  goToPage(index: number) {
    if (index >= 0 && index < this.pages.length) {
      if (this.currentPageIndex !== index) {
        this.currentPageIndex = -1;
        setTimeout(() => {
          this.currentPageIndex = index;
          this.showPageOptions = false;
          this.selectCurrentPage();
        }, 0);
      } else {
        this.showPageOptions = false;
      }
    }
  }

  onTogglePagination() {
    this.showPageOptions = !this.showPageOptions;
  }

  private selectCurrentPage() {
    if (this.pages && this.pages[this.currentPageIndex]) {
      const page = this.pages[this.currentPageIndex];
      const section = this.getFlowchartSection(page.flowchartSectionId);
      if (section) {
        this.eliteFormDesignService.setActiveFormInfo(
          page.eliteFormId,
          page.id,
          section.flowchartId,
          section.id,
          null,
          null
        );
        this.eliteFormDesignService.setActiveCanvasId(page.id);
      }
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.eliteFormDesignService.setActiveFormInfo(null, null, null, null , null, null);
    this.eliteFormDesignService.setActiveCanvasId(null);
  }
}
